import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';


const TechnicalSpecsSection = () => {
  const [dailyUsage, setDailyUsage] = useState(12);
  const [solarPanels, setSolarPanels] = useState(true);
  
  const recommendedCapacity = solarPanels ? dailyUsage * 0.7 : dailyUsage * 0.5;

  const specifications = [
    {
      category: 'Capaciteit',
      icon: 'Battery',
      items: [
        { label: 'Gebruikelijke capaciteit', value: '5-20 kWh' },
        { label: 'Bruikbare capaciteit', value: '90-95% van totaal' },
        { label: 'Modulair uitbreidbaar', value: 'Ja, bij meeste systemen' }
      ]
    },
    {
      category: 'Prestaties',
      icon: 'Zap',
      items: [
        { label: 'Laad/ontlaad vermogen', value: '3-5 kW continu' },
        { label: 'Piek vermogen', value: 'Tot 10 kW kortstondig' },
        { label: 'Responstijd', value: '< 10 milliseconden' }
      ]
    },
    {
      category: 'Levensduur',
      icon: 'Clock',
      items: [
        { label: 'Aantal cycli', value: '6.000-15.000' },
        { label: 'Garantie', value: '10 jaar standaard' },
        { label: 'Verwachte levensduur', value: '15-20 jaar' }
      ]
    },
    {
      category: 'Veiligheid',
      icon: 'Shield',
      items: [
        { label: 'Certificering', value: 'CE, IEC 62619' },
        { label: 'Brandbeveiliging', value: 'Geïntegreerd BMS' },
        { label: 'Temperatuurregeling', value: 'Actieve koeling' }
      ]
    }
  ];

  const installationRequirements = [
    {
      icon: 'Home',
      title: 'Ruimte',
      description: 'Meterkast of technische ruimte',
      details: ['60x40x20 cm gemiddeld', 'Droge, vorstvrije ruimte', 'Ventilatie gewenst']
    },
    {
      icon: 'Zap',
      title: 'Elektrische aansluiting',
      description: 'Groepenkast aanpassing vaak nodig',
      details: ['3-fase aansluiting aanbevolen', 'Aparte groep voor batterij', 'Smart meter vereist']
    },
    {
      icon: 'FileText',
      title: 'Vergunningen',
      description: 'Meestal geen vergunning nodig',
      details: ['Melding bij netbeheerder', 'Keuring door installateur', 'Certificaat na installatie']
    }
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Technische specificaties
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Wat u moet weten over capaciteit, prestaties en veiligheid
          </p>
        </motion.div>

        {/* Capacity Calculator */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 mb-12"
        >
          <h3 className="text-2xl font-bold text-foreground mb-6 text-center">Capaciteit calculator</h3>
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl p-6 mb-6">
              <label className="block text-sm font-medium text-foreground mb-3">
                Dagelijks stroomverbruik (kWh)
              </label>
              <input
                type="range"
                min="5"
                max="30"
                value={dailyUsage}
                onChange={(e) => setDailyUsage(Number(e?.target?.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between mt-2">
                <span className="text-sm text-muted-foreground">5 kWh</span>
                <span className="text-lg font-bold text-primary">{dailyUsage} kWh</span>
                <span className="text-sm text-muted-foreground">30 kWh</span>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 mb-6">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={solarPanels}
                  onChange={(e) => setSolarPanels(e?.target?.checked)}
                  className="w-5 h-5 text-green-600 rounded"
                />
                <span className="text-sm font-medium text-foreground">Ik heb zonnepanelen</span>
              </label>
            </div>

            <div className="bg-green-600 text-white rounded-xl p-6 text-center">
              <p className="text-sm mb-2">Aanbevolen batterijcapaciteit</p>
              <p className="text-4xl font-bold mb-2">{recommendedCapacity?.toFixed(1)} kWh</p>
              <p className="text-sm opacity-90">
                {solarPanels 
                  ? 'Optimaal voor opslag van zonne-energie overschot' :'Geschikt voor peak shaving en back-up'}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Technical Specs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {specifications?.map((spec, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-xl p-6 shadow-elevation-1"
            >
              <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name={spec?.icon} size={24} className="text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-4">{spec?.category}</h3>
              <ul className="space-y-3">
                {spec?.items?.map((item, idx) => (
                  <li key={idx}>
                    <p className="text-xs text-muted-foreground mb-1">{item?.label}</p>
                    <p className="text-sm font-semibold text-foreground">{item?.value}</p>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Installation Requirements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h3 className="text-2xl font-bold text-foreground mb-8 text-center">Installatie-eisen</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {installationRequirements?.map((req, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-elevation-1">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={req?.icon} size={24} className="text-primary" />
                </div>
                <h4 className="text-lg font-semibold text-foreground mb-2">{req?.title}</h4>
                <p className="text-sm text-muted-foreground mb-4">{req?.description}</p>
                <ul className="space-y-2">
                  {req?.details?.map((detail, idx) => (
                    <li key={idx} className="flex items-start space-x-2">
                      <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-muted-foreground">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TechnicalSpecsSection;